import React from 'react';
import { restaurantInfo, translations } from '../data/mock';
import { Phone, MapPin, Heart, Facebook } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const { language } = useLanguage();
  const t = translations[language];

  const navLinks = [
    { name: t.nav.home, href: '#home' },
    { name: t.nav.about, href: '#chi-siamo' },
    { name: t.nav.menu, href: '#menu' },
    { name: t.nav.specials, href: '#specialita' },
    { name: t.nav.hours, href: '#orari' },
    { name: t.nav.contact, href: '#contatti' }
  ];

  return (
    <footer className="bg-[#2a1810] text-white relative overflow-hidden">
      {/* Decorative top border */}
      <div className="h-0.5 bg-[#c9a227]"></div>

      {/* Main footer */}
      <div className="container mx-auto px-4 py-16">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand column */}
          <div className="lg:col-span-2">
            <div className="flex items-center gap-4 mb-6">
              <div>
                <h3 className="font-serif text-2xl font-bold text-white">Mezzaluna</h3>
                <p className="text-[#c9a227] text-xs uppercase tracking-[0.2em]">Ristorante</p>
                <p className="text-white/60 text-sm italic">{restaurantInfo.subtitle[language]}</p>
              </div>
            </div>
            <p className="text-white/60 mb-6 max-w-md leading-relaxed">
              {t.footer.description}
            </p>
            <div className="flex items-center gap-3">
              <a 
                href={restaurantInfo.facebookUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="w-10 h-10 border border-white/20 flex items-center justify-center hover:border-[#c9a227] hover:text-[#c9a227] transition-all duration-300"
              >
                <Facebook size={18} />
              </a>
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h4 className="font-serif text-lg font-bold mb-6 text-white">{t.footer.quickLinks}</h4>
            <ul className="space-y-3">
              {navLinks.map(link => (
                <li key={link.name}>
                  <a 
                    href={link.href}
                    className="text-white/60 hover:text-[#c9a227] transition-colors text-sm"
                  >
                    {link.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-serif text-lg font-bold mb-6 text-white">{t.footer.contacts}</h4>
            <ul className="space-y-4">
              <li>
                <a 
                  href={`tel:${restaurantInfo.phone}`}
                  className="flex items-center gap-3 text-white/60 hover:text-[#c9a227] transition-colors text-sm"
                >
                  <Phone size={16} className="text-[#c9a227]" />
                  {restaurantInfo.phone}
                </a>
              </li>
              <li className="flex items-start gap-3 text-white/60 text-sm">
                <MapPin size={16} className="text-[#c9a227] mt-0.5 flex-shrink-0" />
                <span>{restaurantInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom bar */}
      <div className="border-t border-white/10">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-white/40 text-sm">
              © {new Date().getFullYear()} Ristorante Mezzaluna. {t.footer.rights}
            </p>
            <p className="text-white/40 text-sm flex items-center gap-2">
              {t.footer.madeWith} <Heart size={12} className="text-[#c75b39]" /> {t.footer.in}
            </p>
          </div>
        </div>
      </div>

      {/* Designer credit */}
      <div className="border-t border-white/10 bg-[#1a0f0a]">
        <div className="container mx-auto px-4 py-4">
          <a 
            href="https://doubleastudio.ch" 
            target="_blank" 
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-3 text-white/50 hover:text-white/80 transition-colors group"
          >
            <span className="text-sm">Designed & Developed by</span>
            <img 
              src="/images/doubleastudio-logo.jpg" 
              alt="doubleAstudio logo" 
              className="h-6 w-6 object-contain rounded group-hover:scale-110 transition-transform"
            />
            <span className="text-sm font-medium">doubleAstudio</span>
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
